import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  CardContent,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Snackbar,
  ListItemIcon,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle,
  MoreVert,
  Warning,
  PlayArrow,
  Stop,
  Delete,
  Update,
  Add,
  Refresh,
  Download,
  Shield,
  Mail,
  Search,
  Block,
  Star,
} from "@mui/icons-material";
import {
  fetchPlugins,
  installPlugin,
  activatePlugin,
  deactivatePlugin,
  deletePlugin,
  updatePlugin,
} from "../../redux/slices/pluginsSlice";

// Recommended plugins list
const RECOMMENDED_PLUGINS = [
  {
    slug: "cleantalk-spam-protect",
    name: "Anti-Spam by CleanTalk",
    description:
      "Universal anti-spam protection for comments, registrations, and forms",
    icon: <Block sx={{ color: "#e53935" }} />,
  },
  {
    slug: "seo-by-rank-math",
    name: "Rank Math SEO",
    description:
      "Complete SEO solution with schema markup and analytics integration",
    icon: <Search sx={{ color: "#1e88e5" }} />,
  },
  {
    slug: "security-malware-firewall",
    name: "Security by CleanTalk",
    description: "Malware scanner, firewall, and brute-force protection",
    icon: <Shield sx={{ color: "#43a047" }} />,
  },
  {
    slug: "wp-mail-smtp",
    name: "WP Mail SMTP",
    description: "Reliable email delivery via SMTP with logging",
    icon: <Mail sx={{ color: "#fb8c00" }} />,
  },
  {
    slug: "gravityforms",
    name: "Gravity Forms",
    description:
      "Premium form builder — requires license (install manually via ZIP upload)",
    icon: <Star sx={{ color: "#7b1fa2" }} />,
    premium: true,
  },
];

/**
 * Site plugins tab showing installed plugins
 */
const SitePluginsTab = ({ site }) => {
  const dispatch = useDispatch();
  const { plugins, loading, error, actionLoading } = useSelector(
    (state) => state.plugins,
  );

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedPlugin, setSelectedPlugin] = useState(null);
  const [installDialogOpen, setInstallDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pluginToInstall, setPluginToInstall] = useState("");
  const [activateOnInstall, setActivateOnInstall] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [installingRecommended, setInstallingRecommended] = useState({});

  useEffect(() => {
    if (site?.id) {
      dispatch(fetchPlugins(site.id));
    }
  }, [site?.id, dispatch]);

  const handleMenuOpen = (event, plugin) => {
    setMenuAnchor(event.currentTarget);
    setSelectedPlugin(plugin);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleRefresh = () => {
    if (site?.id) {
      dispatch(fetchPlugins(site.id));
    }
  };

  const handleActivate = async () => {
    handleMenuClose();
    try {
      const result = await dispatch(
        activatePlugin({ siteId: site.id, plugin: selectedPlugin.name }),
      ).unwrap();

      setSnackbar({
        open: true,
        message: result.message || "Plugin activated successfully",
        severity: "success",
      });

      // Refresh plugin list
      dispatch(fetchPlugins(site.id));
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || "Failed to activate plugin",
        severity: "error",
      });
    }
  };

  const handleDeactivate = async () => {
    handleMenuClose();
    try {
      const result = await dispatch(
        deactivatePlugin({ siteId: site.id, plugin: selectedPlugin.name }),
      ).unwrap();

      setSnackbar({
        open: true,
        message: result.message || "Plugin deactivated successfully",
        severity: "success",
      });

      // Refresh plugin list
      dispatch(fetchPlugins(site.id));
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || "Failed to deactivate plugin",
        severity: "error",
      });
    }
  };

  const handleUpdate = async () => {
    handleMenuClose();
    try {
      const result = await dispatch(
        updatePlugin({ siteId: site.id, plugin: selectedPlugin.name }),
      ).unwrap();

      setSnackbar({
        open: true,
        message: result.message || "Plugin updated successfully",
        severity: "success",
      });

      // Refresh plugin list
      dispatch(fetchPlugins(site.id));
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || "Failed to update plugin",
        severity: "error",
      });
    }
  };

  const handleOpenDeleteDialog = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      const result = await dispatch(
        deletePlugin({ siteId: site.id, plugin: selectedPlugin.name }),
      ).unwrap();

      setSnackbar({
        open: true,
        message: result.message || "Plugin deleted successfully",
        severity: "success",
      });

      setDeleteDialogOpen(false);
      // Refresh plugin list
      dispatch(fetchPlugins(site.id));
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || "Failed to delete plugin",
        severity: "error",
      });
    }
  };

  const handleInstall = async () => {
    if (!pluginToInstall.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter a plugin name",
        severity: "error",
      });
      return;
    }

    try {
      const result = await dispatch(
        installPlugin({
          siteId: site.id,
          plugin: pluginToInstall.trim(),
          activate: activateOnInstall,
        }),
      ).unwrap();

      setSnackbar({
        open: true,
        message: result.message || "Plugin installed successfully",
        severity: "success",
      });

      setInstallDialogOpen(false);
      setPluginToInstall("");
      setActivateOnInstall(true);
      // Refresh plugin list
      dispatch(fetchPlugins(site.id));
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || "Failed to install plugin",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleInstallRecommended = async (slug) => {
    setInstallingRecommended((prev) => ({ ...prev, [slug]: true }));
    try {
      const result = await dispatch(
        installPlugin({ siteId: site.id, plugin: slug, activate: true }),
      ).unwrap();

      setSnackbar({
        open: true,
        message: result.message || `${slug} installed successfully`,
        severity: "success",
      });

      dispatch(fetchPlugins(site.id));
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || `Failed to install ${slug}`,
        severity: "error",
      });
    } finally {
      setInstallingRecommended((prev) => ({ ...prev, [slug]: false }));
    }
  };

  const installedSlugs = plugins.map((p) => p.name);

  const isActive = (status) => status === "active";
  const hasUpdate = (update) => update === "available";

  if (loading && plugins.length === 0) {
    return (
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      </CardContent>
    );
  }

  return (
    <CardContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Installed Plugins ({plugins.length})
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={handleRefresh}
            disabled={loading}
            size="small"
            sx={{ mr: 1 }}
          >
            <Refresh />
          </IconButton>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Add />}
            onClick={() => setInstallDialogOpen(true)}
          >
            Install Plugin
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {plugins.length === 0 ? (
        <Alert severity="info">No plugins installed</Alert>
      ) : (
        <List>
          {plugins.map((plugin, index) => (
            <React.Fragment key={plugin.name}>
              <ListItem
                sx={{ px: 0 }}
                secondaryAction={
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {hasUpdate(plugin.update) && (
                      <Chip
                        label="Update Available"
                        size="small"
                        color="warning"
                      />
                    )}
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, plugin)}
                      disabled={actionLoading}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {plugin.name}
                      </Typography>
                      {isActive(plugin.status) ? (
                        <CheckCircle
                          sx={{ fontSize: 18, color: "success.main" }}
                        />
                      ) : (
                        <Warning
                          sx={{ fontSize: 18, color: "text.disabled" }}
                        />
                      )}
                    </Box>
                  }
                  secondary={`Version ${plugin.version} • ${plugin.status}`}
                />
              </ListItem>
              {index < plugins.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Recommended Plugins */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Recommended Plugins
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          One-click install for essential WordPress plugins
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {RECOMMENDED_PLUGINS.map((rec) => {
            const isInstalled = installedSlugs.includes(rec.slug);
            const isInstalling = installingRecommended[rec.slug];
            return (
              <Paper
                key={rec.slug}
                variant="outlined"
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  opacity: isInstalled ? 0.7 : 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {rec.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={500}>
                    {rec.name}
                    {rec.premium && (
                      <Chip
                        label="Premium"
                        size="small"
                        color="secondary"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {rec.description}
                  </Typography>
                </Box>
                {isInstalled ? (
                  <Chip
                    icon={<CheckCircle />}
                    label="Installed"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                ) : rec.premium ? (
                  <Tooltip title="Upload this plugin manually via WP Admin → Plugins → Add New → Upload">
                    <Chip
                      label="Manual Install"
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      isInstalling ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Download />
                      )
                    }
                    onClick={() => handleInstallRecommended(rec.slug)}
                    disabled={isInstalling || actionLoading}
                  >
                    {isInstalling ? "Installing..." : "Install"}
                  </Button>
                )}
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* Plugin Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {selectedPlugin && !isActive(selectedPlugin.status) && (
          <MenuItem onClick={handleActivate}>
            <ListItemIcon>
              <PlayArrow fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Activate" />
          </MenuItem>
        )}
        {selectedPlugin && isActive(selectedPlugin.status) && (
          <MenuItem onClick={handleDeactivate}>
            <ListItemIcon>
              <Stop fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Deactivate" />
          </MenuItem>
        )}
        {selectedPlugin && hasUpdate(selectedPlugin.update) && (
          <MenuItem onClick={handleUpdate}>
            <ListItemIcon>
              <Update fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Update" />
          </MenuItem>
        )}
        <MenuItem onClick={handleOpenDeleteDialog}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>

      {/* Install Plugin Dialog */}
      <Dialog
        open={installDialogOpen}
        onClose={() => setInstallDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Install Plugin</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Plugin Slug"
            placeholder="e.g., akismet, woocommerce"
            value={pluginToInstall}
            onChange={(e) => setPluginToInstall(e.target.value)}
            margin="normal"
            helperText="Enter the plugin slug from WordPress.org"
            autoFocus
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={activateOnInstall}
                onChange={(e) => setActivateOnInstall(e.target.checked)}
              />
            }
            label="Activate plugin after installation"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstallDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInstall}
            disabled={actionLoading || !pluginToInstall.trim()}
          >
            {actionLoading ? "Installing..." : "Install"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>Delete Plugin</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete{" "}
            <strong>{selectedPlugin?.name}</strong>? This will permanently
            remove the plugin and its data.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={actionLoading}
          >
            {actionLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </CardContent>
  );
};

export default SitePluginsTab;
